
import { supabase } from '../lib/supabase';
import { ReportFilters } from '../types';

export interface SummaryMetrics {
  total_served: number;
  registered: number;
  headcount: number;
  engagements: number;
  staff: number;
  volunteers: number;
  volunteer_hours: number;
  coaching_hours: number;
  instructional_hours?: number;
}

export interface AudienceBreakdown {
  label: string;
  registered: number;
  headcount: number;
  total: number;
}

export interface MonthlyTrend {
  month: string;
  count: number;
}

export interface ProgramMix {
  name: string;
  count: number;
}

export interface VolunteerMix {
  name: string;
  count: number;
}

export interface RegionalReach {
  label: string;
  registered: number;
  headcount: number;
  total: number;
}

export interface ImpactReport {
  summary: SummaryMetrics;
  audience_breakdown: AudienceBreakdown[];
  regional_reach?: RegionalReach[];
  monthly_trend: MonthlyTrend[];
  program_mix: ProgramMix[];
  volunteer_mix: VolunteerMix[];
}

export interface AuditRecord {
  id: string;
  name: string;
  detail: string;
  date: string;
  category: string;
}

export const reportService = {
  async getImpactReport(filters: ReportFilters): Promise<ImpactReport> {
    const { data, error } = await supabase.rpc('get_impact_report', {
      p_start_date: filters.startDate,
      p_end_date: filters.endDate,
      p_program_ids: filters.programIds?.length ? filters.programIds : null,
      p_audience_ids: filters.audienceIds?.length ? filters.audienceIds : null
    });

    if (error) {
      console.error('RPC Error (get_impact_report):', error);
      throw error;
    }
    
    // Ensure arrays are present even if null from RPC
    const report = (data || {}) as any;
    return {
      summary: report.summary || { total_served: 0, registered: 0, headcount: 0, engagements: 0, staff: 0, volunteers: 0, volunteer_hours: 0, coaching_hours: 0, instructional_hours: 0 },
      audience_breakdown: report.audience_breakdown || [],
      regional_reach: report.regional_reach || [],
      monthly_trend: report.monthly_trend || [],
      program_mix: report.program_mix || [],
      volunteer_mix: report.volunteer_mix || []
    };
  },

  async getImpactAudit(
    metricType: 'registered' | 'headcount' | 'staff' | 'engagements' | 'volunteers' | 'coaching_hours' | 'instructional_hours',
    filters: ReportFilters
  ): Promise<AuditRecord[]> {
    const { data, error } = await supabase.rpc('get_impact_audit', {
      p_metric_type: metricType,
      p_start_date: filters.startDate,
      p_end_date: filters.endDate,
      p_program_ids: filters.programIds?.length ? filters.programIds : null,
      p_audience_ids: filters.audienceIds?.length ? filters.audienceIds : null
    });

    if (error) {
      console.error('RPC Error (get_impact_audit):', error);
      throw error;
    }
    return (data || []) as AuditRecord[];
  },

  // Legacy compatibility wrappers if needed by other components
  async getSummaryMetrics(filters: ReportFilters): Promise<any> {
    const report = await this.getImpactReport(filters);
    return {
      totalPeople: report.summary.total_served,
      totalOrganizations: 0, // Not used in new design
      totalPrograms: 0, // Not used in new design
      activeEngagements: report.summary.engagements,
      peopleTrend: { value: 0, isPositive: true },
      orgTrend: { value: 0, isPositive: true }
    };
  },

  async getPeopleServed(filters: ReportFilters): Promise<any> {
    const report = await this.getImpactReport(filters);
    return {
      total: report.summary.total_served,
      enrollment: report.summary.registered,
      headcount: report.summary.headcount,
      staffCount: report.summary.staff,
      breakdown: report.audience_breakdown
    };
  },

  async getEngagementOverTime(filters: ReportFilters): Promise<any[]> {
    const report = await this.getImpactReport(filters);
    return report.monthly_trend.map(t => ({ name: t.month, value: t.count }));
  },

  async getProgramDistribution(filters: ReportFilters): Promise<any[]> {
    const report = await this.getImpactReport(filters);
    return report.program_mix.map(p => ({ name: p.name, value: p.count }));
  }
};
